import { createFileRoute } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { ErrorState, LoadingState, StatRow } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";

import { ExpenseSummaryCard } from "../../../components/dashboard/expense-summary-card.js";
import { HealthFigures } from "../../../components/dashboard/health-figures.js";
import {
  HealthFilters,
  type HealthFilterState,
} from "../../../components/dashboard/health-filters.js";
import { IncomeFunnel } from "../../../components/dashboard/income-funnel.js";
import { FreshnessPanel } from "../../../components/dashboard/freshness-panel.js";
import { NetWorthCard } from "../../../components/dashboard/net-worth-card.js";
import { NetWorthChart } from "../../../components/dashboard/net-worth-chart.js";
import { NetWorthDonut } from "../../../components/dashboard/net-worth-donut.js";
import { StillToGoOutCard } from "../../../components/dashboard/still-to-go-out-card.js";
import { RecentActivityCard } from "../../../components/dashboard/recent-activity-card.js";
import { SurplusChart } from "../../../components/dashboard/surplus-chart.js";
import {
  useDashboard,
  useDashboardHealth,
  useHouseholdMembers,
} from "../../../components/dashboard/use-dashboard.js";
import { Page } from "../../../components/page.js";

export const Route = createFileRoute("/_authenticated/_shell/")({
  component: Dashboard,
});

/**
 * The Dashboard — "where do we stand financially, right now?".
 *
 * ==========================================================================================
 * FIGURES FIRST, THEN THE SAME QUESTIONS AS PICTURES
 * ==========================================================================================
 *
 * The page opens with a row of four figures — net worth, in, out, left — and everything below it is one
 * of those figures drawn out over time or broken apart. A reader who wants only the numbers is done in
 * the first screen; a reader who wants to know whether a number is normal keeps going.
 *
 * THIS REPLACED A TWO-COLUMN LAYOUT, and the reason it did is worth keeping rather than quietly
 * dropping. That layout put the BALANCE in the left column and the FLOW in the right, each with its
 * chart directly beneath its card, so that "nothing in the grid invites adding a figure from one to a
 * figure from the other". It was a good rule and the geometry did carry it.
 *
 * What it could not do is lead with the figures. Four cards in a row, all the same size and type scale,
 * is the shape a household actually opens the page for — and it is also, incidentally, a better answer
 * to the peer requirement that layout was enforcing by hand: `StatRow` makes them peers by construction
 * rather than by two cards being carefully matched.
 *
 * The balance/flow separation survives as VERTICAL BANDS instead of columns. Net worth and its chart are
 * one band; the three flow figures, the surplus history and the funnel are another; the expense
 * ranking is the detail behind one of them. What is gone is the guarantee that a figure and its own
 * history are adjacent — a KPI row cannot give that, since it groups all four figures — so the charts
 * below appear in the SAME ORDER as the cards above, and each names its own subject and window.
 *
 * ==========================================================================================
 * TWO INDEPENDENT READS
 * ==========================================================================================
 *
 * Each renders its own state. One `isPending` over both would make a filter change blank the net worth
 * card, which is the visible half of the thing AC-D24 forbids.
 */
function Dashboard(): ReactNode {
  const t = useT();
  const dashboard = useDashboard();

  /*
   * THE FILTERS ARE THE PAGE'S STATE, and they are the only state on it.
   *
   * They live here rather than in a card because they now govern four separate pieces — the In, Out and
   * Left cards, the funnel and the ranking — and because the query key contains them, so a change is a new
   * cache entry and a new request for those alone. Net worth is deliberately not among them; see
   * `health-filters.tsx` for how that is kept legible now that the control no longer sits on one card.
   */
  const [filters, setFilters] = useState<HealthFilterState>({
    scope: "household",
    period: "this-month",
  });

  const health = useDashboardHealth(filters.scope, filters.period);
  const members = useHouseholdMembers();

  return (
    <Page>
      {/*
       * THE FILTERS SIT IN THE TITLE ROW, and what they do NOT reach is stated by the sections themselves.
       *
       * They were above the KPI row with the label "In, Out and Left for …", which was honest about reach
       * and quiet about importance. At the title row they read as the page's own control, which is nearly
       * true: they govern the three flow figures, the funnel and the expense ranking — five of the
       * page's ten pieces.
       *
       * WHAT THEY CANNOT REACH IS NOT A CHOICE. Net worth has no per-member form at all: `dashboard.md`'s
       * non-goals say a per-member breakdown "would need ownership on investments and properties that
       * nothing records", and "net worth for this month" is not a quantity — a balance is as-of-now. The
       * net worth chart is fixed at twelve months by AC-D3 and the surplus chart at a rolling twelve at
       * household scope by AC-D22/AC-D22a, deliberately, because its job is to say whether this month is
       * normal. Activity and freshness are facts about the data rather than about anyone's money.
       *
       * So each of those five states its own window in its own hint. That is weaker than the geometry that
       * used to carry it — `dashboard-layout.md` records the trade — and it is the price of the control
       * being where a reader looks for it.
       */}
      <Page.Title
        actions={
          <HealthFilters
            value={filters}
            members={members.data?.members ?? []}
            onChange={setFilters}
          />
        }
      >
        {t("nav.dashboard")}
      </Page.Title>
      <Page.Body>
        {/*
         * ① THE FOUR FIGURES. `StatRow` is `auto-fit` with a minimum, so they fill the width they have
         * and break to a second line rather than squeezing a figure until it wraps mid-number.
         *
         * Net worth sits first and is NOT governed by the filters above; the three that follow are. Its
         * hint carries a year-ago comparison, which reads as its own window, and theirs name the scope
         * and period they are showing.
         */}
        <StatRow className="col-span-full">
          {dashboard.isError ? (
            <ErrorState>{t("dash.netWorthFailed")}</ErrorState>
          ) : dashboard.data ? (
            <NetWorthCard netWorth={dashboard.data.netWorth} />
          ) : (
            <LoadingState />
          )}

          {health.isError ? (
            <ErrorState>{t("dash.flowFailed")}</ErrorState>
          ) : (
            <HealthFigures
              health={health.data}
              members={members.data?.members ?? []}
              filters={filters}
            />
          )}
        </StatRow>

        {/*
         * ② NET WORTH OVER TIME, full width and on its own.
         *
         * The page's one balance chart and the only place the household's position over time exists. It
         * is full width because its four layers plus the net worth line are five things to read at once,
         * and because it is the section a household comes back for.
         */}
        {dashboard.isError ? null : dashboard.data ? (
          <div className="col-span-full">
            <NetWorthChart
              chart={dashboard.data.netWorthChart}
              netWorth={dashboard.data.netWorth}
            />
          </div>
        ) : null}

        {/*
         * ③ AND ③b — KEPT EACH MONTH, AND WHAT NET WORTH IS MADE OF.
         *
         * IT WAS HALF OF A PAIR until 2026-08-14, beside a ring that answered "what did this period do".
         * The funnel below answers that better — same three figures, plus where the money actually went —
         * so the ring was retired and this chart took the width. **It is half of a pair again since
         * 2026-08-22**, beside a mark that answers a different question entirely.
         *
         * THE TWO ARE NOT RELATED AND THAT IS FINE HERE. The row below pairs the funnel and the ranking
         * BECAUSE they are the same categories twice and had to be compared by eye. This pair is not that:
         * one is a flow over time, the other a balance decomposed today, and they sit together because
         * each is a half-width shape and the page has no reason to spend a full row on either. The
         * headings carry the subjects, which is what stops the adjacency implying a relationship.
         *
         * NEITHER FOLLOWS THE FILTERS, and both say so. The surplus chart is AC-D22a; the composition is
         * AC-D3, whose non-goals record that a per-member net worth "would need ownership on investments
         * and properties that nothing records". That they agree on it is a coincidence worth not relying
         * on — each states its own scope in its own hint.
         *
         * `lg` AND NOT `md`: a donut needs to be a circle rather than an ellipse of dead space, and the
         * surplus chart's bars need width per month. Below that they stack.
         */}
        {dashboard.isError ? null : dashboard.data ? (
          <div className="col-span-full grid gap-6 lg:grid-cols-2">
            <SurplusChart chart={dashboard.data.surplusChart} />
            <NetWorthDonut netWorth={dashboard.data.netWorth} />
          </div>
        ) : null}

        {/*
         * ④ AND ⑤ — THE SAME CATEGORIES TWICE, SIDE BY SIDE.
         *
         * The funnel answers "where did it go", the ranking answers "which is biggest". They read the same
         * `expenseSummary` and each was full width until 2026-08-14, one above the other — which put the
         * page's two most closely related sections a scroll apart and made the household compare them from
         * memory. In one row they are compared by eye, which is the whole reason both are kept rather than
         * one replacing the other.
         *
         * THE FUNNEL IS FIRST because it states the total and the remainder; the ranking is the detail
         * inside it. Left to right is the order the pair is read in, and it is the order they had when the
         * pair was a column.
         *
         * THE TWO CARDS ARE THE SAME HEIGHT, which is a correction rather than a preference. This row was
         * `items-start` for a few hours, on the argument that stretching the shorter card would centre its
         * chart in a box of dead space. That argument was WRONG about the markup: `SectionCard` is a block,
         * its children flow from the top, and a stretched card simply has its spare room at the bottom —
         * nothing moves and nothing is centred. What `items-start` actually produced was two panels of
         * different heights side by side, which reads as one of them having failed to finish rendering.
         *
         * `lg` AND NOT `md`, because `RankedBarChartImpl` reserves 140px for the category labels and 72px
         * for the value labels — 212px of any width it is given is furniture, not plot. At `lg` the column
         * is ~476px and the plot ~264px, which was measured rather than assumed; at `md` the pair stacks
         * instead, because a bar needs to be a length rather than a stub.
         */}
        {health.isError ? null : (
          <div className="col-span-full grid gap-6 lg:grid-cols-2">
            <IncomeFunnel
              health={health.data}
              members={members.data?.members ?? []}
              filters={filters}
            />

            {health.data === undefined ? null : (
              <ExpenseSummaryCard
                summary={health.data.expenseSummary}
                filters={filters}
                members={members.data?.members ?? []}
              />
            )}
          </div>
        )}

        {/*
         * ⑨ AND ⑩, what is happening — the activity of the last days and the payments of the coming ones.
         * Neither is a balance nor a flow, and both are context rather than an answer, which is why they sit
         * below the fold together (FR-085). (This comment numbered them ⑤ and ⑥ until 024: the pieces were
         * renumbered in `dashboard-layout.md` when the health ring was retired, and the comment was not.)
         *
         * ⑩ IS "STILL TO GO OUT" (feature 024), beside activity and NOT in the row of figures — see its own
         * header for why a cash figure cannot sit with `Out`. It follows BOTH filters (amended 2026-10-26): the
         * period filter's month and year are the schedule's own.
         *
         * EQUAL HEIGHTS, for the same reason as the pair above: two panels ending at different points looks
         * like a fault rather than like a consequence of their contents.
         *
         * ⑪, DATA FRESHNESS, takes its own full-width row below them: the page's footnote, last because it
         * qualifies everything above it rather than adding to it.
         */}
        {dashboard.isError ? null : dashboard.data ? (
          <>
            <div className="col-span-full grid gap-6 md:grid-cols-2">
              <RecentActivityCard entries={dashboard.data.recentActivity} />
              <StillToGoOutCard filters={filters} members={members.data?.members ?? []} />
            </div>
            <div className="col-span-full">
              <FreshnessPanel rows={dashboard.data.freshness} />
            </div>
          </>
        ) : null}
      </Page.Body>
    </Page>
  );
}
